import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { Modal, Button } from '@core/components/ui';

/**
 * MoveFileModal Component
 *
 * Modal dialog for moving files to a different folder.
 * Shows available folders as selectable options.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onMove - Callback to move file (receives folderId or null)
 * @param {Array} props.folders - Available folders
 * @param {string|null} props.currentFolderId - Current folder ID (to exclude from list)
 * @param {string} props.fileName - Name of file being moved
 * @param {boolean} [props.isMoving=false] - Whether move is in progress
 */
const MoveFileModal = ({ isOpen, onClose, onMove, folders, currentFolderId, fileName, isMoving = false }) => {
  const { t } = useTranslation();
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  // Handle move
  const handleMove = useCallback(async () => {
    try {
      await onMove(selectedFolderId);
      // On success, reset and close
      setSelectedFolderId(null);
    } catch (err) {
      // Error handling is done in parent component
    }
  }, [selectedFolderId, onMove]);

  // Handle close
  const handleClose = useCallback(() => {
    setSelectedFolderId(null);
    onClose();
  }, [onClose]);

  // Filter out current folder if we're already in a folder
  const availableFolders = folders.filter(folder => folder.id !== currentFolderId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('files.explorer.moveFileTitle', 'Mover archivo')}
      size="md"
      closeButton
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isMoving}
            className="cursor-pointer"
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            onClick={handleMove}
            disabled={isMoving || selectedFolderId === undefined}
            className="cursor-pointer"
          >
            {isMoving ? (
              <>
                <Icon icon="eos-icons:loading" className="w-5 h-5 mr-2" />
                {t('common.moving', 'Moviendo...')}
              </>
            ) : (
              <>
                <Icon icon="mdi:folder-move" className="w-5 h-5 mr-2" />
                {t('files.explorer.move', 'Mover')}
              </>
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* File name */}
        <div className="text-sm text-muted">
          {t('files.explorer.movingFile', 'Moviendo: ')}
          <span className="font-medium text-default">{fileName}</span>
        </div>

        {/* Folder selection */}
        <div>
          <label className="block text-sm font-medium text-default mb-2">
            {t('files.explorer.selectDestination', 'Selecciona carpeta destino:')}
          </label>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {/* Root option (no folder) */}
            <button
              type="button"
              className={`w-full flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                selectedFolderId === null
                  ? 'border-brand bg-brand/5 text-brand'
                  : 'border-border-default hover:bg-brand/5 text-default'
              }`}
              onClick={() => setSelectedFolderId(null)}
            >
              <Icon icon="mdi:home" className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                {t('files.explorer.rootFolder', 'Carpeta raíz (sin carpeta)')}
              </span>
              {selectedFolderId === null && (
                <Icon icon="mdi:check-circle" className="w-5 h-5 ml-auto" />
              )}
            </button>

            {/* Available folders */}
            {availableFolders.length > 0 ? (
              availableFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                    selectedFolderId === folder.id
                      ? 'border-brand bg-brand/5 text-brand'
                      : 'border-border-default hover:bg-brand/5 text-default'
                  }`}
                  onClick={() => setSelectedFolderId(folder.id)}
                >
                  <Icon icon="mdi:folder" className="w-5 h-5 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium">{folder.name}</div>
                    {folder.file_count !== undefined && (
                      <div className="text-xs text-muted">
                        {folder.file_count} {t('files.explorer.files', 'archivos')}
                      </div>
                    )}
                  </div>
                  {selectedFolderId === folder.id && (
                    <Icon icon="mdi:check-circle" className="w-5 h-5 ml-auto" />
                  )}
                </button>
              ))
            ) : (
              <div className="text-center text-sm text-muted py-4">
                {t('files.explorer.noFoldersAvailable', 'No hay carpetas disponibles')}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

MoveFileModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      file_count: PropTypes.number,
    })
  ).isRequired,
  currentFolderId: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  isMoving: PropTypes.bool,
};

export { MoveFileModal };
